/**
 * Aba "Controle PLD" da Mesa de Operações (Task #190 Fase 2).
 *
 * Consome /api/v2/pld/* (backend sub-fase 1: canonical_pld_diario, 1,5 ano de
 * SE/S/NE/N). Header com 2 dropdowns (mês de referência — 12 últimos; submercado)
 * + 4 sub-tabs:
 *   - Visão Geral          KPIs + gráfico diário + resumo por semana ONS
 *   - Semanal Detalhado    tabela dia-a-dia com revisão/status
 *   - Comparação Submerc.  4 séries diárias + cross por semana (Δ vs SE)
 *   - Histórico            12 meses, 4 séries
 *
 * MVP sem projeção/estimativa de fechamento (Fase 3). "Comparação Submercados"
 * substitui o "Dessem vs Decomp" do protótipo (sem Decomp ainda). Import de
 * XLSX segue via curl (UI de upload é futura).
 */
import { useState } from "react";
import type { PldSubmercado } from "../../api/types";
import { SubTabs, type SubTabSpec } from "../ui/SubTabs";
import { PldVisaoGeralTab } from "./pld/PldVisaoGeralTab";
import { PldSemanalTab } from "./pld/PldSemanalTab";
import { PldComparacaoTab } from "./pld/PldComparacaoTab";
import { PldHistoricoTab } from "./pld/PldHistoricoTab";
import { PldExportControl } from "./pld/PldExportControl";
import { SUBMERCADO_LABEL, SUBMERCADOS, mesRefCorrente, ultimosMeses } from "./pld/pldShared";

type PldSubTab = "visao" | "semanal" | "comparacao" | "historico";

const SUB_TABS: SubTabSpec<PldSubTab>[] = [
  { id: "visao", label: "Visão Geral" },
  { id: "semanal", label: "Semanal Detalhado" },
  { id: "comparacao", label: "Comparação Submercados" },
  { id: "historico", label: "Histórico" },
];

const MESES_DROPDOWN = ultimosMeses(12);

const SELECT_CLS =
  "bg-indra-azul-deep border border-white/10 rounded-lg px-3 py-1.5 text-xs " +
  "text-white/80 [color-scheme:dark] focus:outline-none focus:border-indra-turquesa/50";

export function ControlePldPage() {
  const [subtab, setSubtab] = useState<PldSubTab>("visao");
  const [mesRef, setMesRef] = useState<string>(mesRefCorrente());
  const [submercado, setSubmercado] = useState<PldSubmercado>("SE");

  return (
    <div data-testid="controle-pld-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-white font-semibold">Controle PLD — CCEE</h2>
          <p className="text-white/50 text-xs mt-1">
            PLD médio diário por submercado (Dados Abertos CCEE). Realizado — sem
            projeção de fechamento.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={mesRef}
            onChange={(e) => setMesRef(e.target.value)}
            data-testid="pld-mes-select"
            aria-label="Mês de referência"
            className={SELECT_CLS}
          >
            {MESES_DROPDOWN.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <select
            value={submercado}
            onChange={(e) => setSubmercado(e.target.value as PldSubmercado)}
            data-testid="pld-submercado-select"
            aria-label="Submercado"
            className={SELECT_CLS}
          >
            {SUBMERCADOS.map((s) => (
              <option key={s} value={s}>{SUBMERCADO_LABEL[s]} ({s})</option>
            ))}
          </select>
          <PldExportControl mesRef={mesRef} submercado={submercado} />
        </div>
      </div>

      <SubTabs tabs={SUB_TABS} active={subtab} onChange={setSubtab} />

      <div className="mt-6">
        {subtab === "visao" && (
          <PldVisaoGeralTab mesRef={mesRef} submercado={submercado} />
        )}
        {subtab === "semanal" && (
          <PldSemanalTab mesRef={mesRef} submercado={submercado} />
        )}
        {subtab === "comparacao" && <PldComparacaoTab mesRef={mesRef} />}
        {subtab === "historico" && <PldHistoricoTab />}
      </div>
    </div>
  );
}
